import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Nav } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import urls from "../../../../../Utils/urls";
import { commonToast } from "../../../../../Utils";
import { getHeaders } from "../../../../Documentation/utils";
import ManualDrawTab from "./ManualDrawTab";
import ExcelUploadTab from "./ExcelUploadTab";
import { validateRows } from "./validateRows";

const parseMoney = (value) => {
  const parsed = parseFloat(`${value ?? ""}`.replace(/[$,\s]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};
const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const computeAvailableFunds = (loanAmount, upb, availableFunds) => {
  const la = parseMoney(loanAmount);
  const balance = parseMoney(upb);
  if (la === null || balance === null)
    return { value: null, formatted: undefined };
  const diff = la - balance;
  return { value: availableFunds, formatted: moneyFormatter.format(availableFunds) };
};

const PENDING_STATUSES = new Set(["SUBMITTED", "MANUAL"]);

// Max length for the client's free-text note/instructions on a draw (manual + excel).
// Centralized here so both tabs and the submit payload enforce the same cap.
const NOTE_MAX_LENGTH = 500;
const cleanNote = (value) => `${value ?? ""}`.trim().slice(0, NOTE_MAX_LENGTH);

// A masquerading admin/support user can view another user's account but must not
// be able to submit a binding draw request on their behalf. Centralized here so the
// same copy is used for the toast guard and the disabled-button tooltips.
const MASQUERADE_SUBMIT_BLOCKED =
  "Submitting draw requests is disabled while masquerading.";

const buildPendingByLoanRecId = (pendingDraws = []) => {
  const map = new Map();
  pendingDraws.forEach((item) => {
    const rawStatus = `${item?.status || ""}`.toUpperCase();
    if (!PENDING_STATUSES.has(rawStatus)) return;
    const loanRecId = `${item?.loanrecid || item?.loanRecId || ""}`.trim();
    if (!loanRecId) return;
    const amount = parseMoney(
      item?.draw_amount ??
        item?.requested_amount ??
        item?.requestedAmt ??
        item?.amount ??
        item?.drawAmount,
    );
    if (amount === null) return;
    const drawDate =
      item?.request_date ||
      item?.requestDate ||
      item?.creation_date ||
      item?.draw_date ||
      item?.drawDate ||
      "";
    const entry = map.get(loanRecId) || { total: 0, items: [] };
    entry.total += amount;
    entry.items.push({ drawDate, amount });
    map.set(loanRecId, entry);
  });
  return map;
};

const NewDrawRequestModal = ({
  show,
  onHide,
  loanID,
  onSuccess,
  pendingDraws = [],
}) => {
  const {
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({ mode: "onSubmit" });
  const [mode, setMode] = useState("manual");
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [lineItems, setLineItems] = useState([
    { id: Date.now(), budgetLine: null, amount: "" },
  ]);
  const [propertyList, setPropertyList] = useState([]);
  const [propertyAccountList, setPropertyAccountList] = useState([]);
  const [budgetLineOptions, setBudgetLineOptions] = useState([]);
  const [isLoadingProperties, setIsLoadingProperties] = useState(false);
  const [isLoadingLineItems, setIsLoadingLineItems] = useState(false);
  const [drawDate, setDrawDate] = useState("");
  const [selectedDrawDate, setSelectedDrawDate] = useState(null);
  const [manualNote, setManualNote] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [excelRows, setExcelRows] = useState([]);
  const [showExcelConfirm, setShowExcelConfirm] = useState(false);
  const [excelProgress, setExcelProgress] = useState("");
  const userId = localStorage.getItem("apex_userid");
  const accountOptions = useSelector((state) => state.account.accountOptions);
  const isMasquerading = Boolean(
    useSelector((state) => state.RS_Authentication?.masquerade?.isIt),
  );
  const hasProjects = propertyList.length > 0;
  const pendingByLoanRecId = useMemo(
    () => buildPendingByLoanRecId(pendingDraws),
    [pendingDraws],
  );

  const resetAllState = () => {
    setMode("manual");
    setSelectedProperty(null);
    setSelectedAccount(null);
    setLineItems([{ id: Date.now(), budgetLine: null, amount: "" }]);
    setDrawDate("");
    setSelectedDrawDate(null);
    setManualNote("");
    setShowConfirm(false);
    setExcelRows([]);
    setShowExcelConfirm(false);
    setExcelProgress("");
    clearErrors();
  };
  const closeModal = () => {
    resetAllState();
    onHide();
  };

  useEffect(() => {
    if (!show) return;
    (async () => {
      setIsLoadingProperties(true);
      try {
        const allAccountIds =
          accountOptions?.length > 0
            ? accountOptions.map((a) => a.value).join(" ")
            : loanID;
        const res = await axios.post(
          urls.draws.getDrawProperties(),
          { LoanRecID: allAccountIds },
          getHeaders(true),
        );
        const data = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || [];
        setPropertyList(
          data.map((item) => {
            const loanAmount = item["Loan Amount"];
            const unpaidPrincipalBalance = item["Unpaid Principal Balance"];
            const available = computeAvailableFunds(loanAmount, unpaidPrincipalBalance, item["Available funds"]);
            // const available = item["Available funds"];
            const pendingEntry = pendingByLoanRecId.get(
              `${item.LoanRecid || ""}`.trim(),
            );
            const pendingAmount = pendingEntry?.total || 0;
            const pendingItems = pendingEntry?.items || [];
            const adjustedAvailable =
              available.value !== null ? available.value - pendingAmount : null;
            return {
              label: item.Property_Address,
              value: item.Property_Address,
              loanRecId: item.LoanRecid,
              account: item.Account,
              loanAmount,
              unpaidPrincipalBalance,
              availableFunds:
                adjustedAvailable !== null
                  ? moneyFormatter.format(adjustedAvailable)
                  : available.formatted,
              availableFundsValue:
                adjustedAvailable !== null
                  ? adjustedAvailable
                  : available.value,
              originalAvailableFundsValue: available.value,
              originalAvailableFunds: available.formatted,
              pendingAmount,
              pendingAmountFormatted:
                pendingAmount > 0 ? moneyFormatter.format(pendingAmount) : null,
              pendingItems,
            };
          }),
        );
        setPropertyAccountList(
          data.map((item) => ({ label: item.Account, value: item.Account })),
        );
      } catch {
        setPropertyList([]);
        setPropertyAccountList([]);
      } finally {
        setIsLoadingProperties(false);
      }
    })();
    (async () => {
      setIsLoadingLineItems(true);
      try {
        const res = await axios.get(urls.draws.getLineItems(), getHeaders());
        setBudgetLineOptions(
          (res?.data?.items || []).map((i) => ({
            label: i.display_value,
            value: i.return_value,
          })),
        );
      } catch {
        setBudgetLineOptions([]);
      } finally {
        setIsLoadingLineItems(false);
      }
    })();
  }, [show, loanID, accountOptions, pendingByLoanRecId]);

  useEffect(() => {
    if (!selectedProperty) return setSelectedAccount(null);
    setSelectedAccount(
      propertyAccountList.find(
        (account) => account.value === selectedProperty.account,
      ) || null,
    );
  }, [selectedProperty, propertyAccountList]);

  useEffect(() => {
    if (excelRows.length === 0 || isLoadingProperties) return;
    setExcelRows((prev) =>
      validateRows(prev, propertyList).map((row, i) => ({
        ...row,
        submitStatus: prev[i]?.submitStatus || "pending",
        submitError: prev[i]?.submitError || "",
      })),
    );
  }, [isLoadingProperties, propertyList]);

  const updateLineItem = (id, updates) =>
    setLineItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item)),
    );
  const handleAmountChange = (id, rawValue, index) => {
    updateLineItem(id, {
      amount: `${rawValue}`.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1"),
    });
    clearErrors(`lineItems.${index}.amount`);
    clearErrors("totalAmount");
  };
  const handleAmountBlur = (id, rawValue) => {
    if (!rawValue) return;
    const numeric = Number(rawValue);
    if (!Number.isNaN(numeric))
      updateLineItem(id, { amount: numeric.toFixed(2) });
  };

  const handleSubmitRequest = async () => {
    if (isMasquerading) {
      toast.error(MASQUERADE_SUBMIT_BLOCKED, { ...commonToast });
      return;
    }
    setSubmitLoader(true);
    try {
      // One note for the whole manual draw request — copied onto every line item
      // so the backend stores it regardless of which line it reads.
      const requestNote = cleanNote(manualNote);
      await axios.post(
        urls.draws.submitDrawRequest(),
        {
          line_items: lineItems.map((item) => ({
            lineId: item.budgetLine?.value,
            requestedAmt: Number(item.amount),
            property: selectedProperty?.value || "",
            loanNumber: selectedAccount?.value || "",
            drawNumber: "",
            notes: requestNote,
            loanrecid: selectedProperty?.loanRecId,
            draw_date: drawDate
              ? `${drawDate} ${format(new Date(), "HH:mm:ss")}`
              : "",
          })),
          user_id: userId,
        },
        getHeaders(),
      );
      toast.success("Draw Request submitted successfully!", { ...commonToast });
      setShowConfirm(false);
      closeModal();
      if (onSuccess) onSuccess();
    } catch {
      toast.error("Something went wrong while submitting the draw request!", {
        ...commonToast,
      });
    } finally {
      setSubmitLoader(false);
    }
  };

  const handleManualSubmit = () => {
    clearErrors();
    let isValid = true;
    if (!selectedProperty) {
      setError("selectedProperty", {
        type: "required",
        message: "Property / Project is required.",
      });
      isValid = false;
    }
    if (!drawDate) {
      setError("drawDate", {
        type: "required",
        message: "Draw Date is required.",
      });
      isValid = false;
    } else if (selectedDrawDate) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (selectedDrawDate.getTime() > today.getTime()) {
        setError("drawDate", {
          type: "validate",
          message: "Draw Date cannot be in the future.",
        });
        isValid = false;
      }
    }
    lineItems.forEach((item, index) => {
      if (!item.budgetLine) {
        setError(`lineItems.${index}.budgetLine`, {
          type: "required",
          message: "Line Item is required.",
        });
        isValid = false;
      }
      if (!item.amount || Number(item.amount) <= 0) {
        setError(`lineItems.${index}.amount`, {
          type: "required",
          message: "Amount is required.",
        });
        isValid = false;
      }
    });
    const availableFundsValue = selectedProperty?.availableFundsValue;
    if (
      isValid &&
      typeof availableFundsValue === "number" &&
      Number.isFinite(availableFundsValue) &&
      manualTotalAmount > availableFundsValue
    ) {
      const formatted = moneyFormatter.format(availableFundsValue);
      setError("totalAmount", {
        type: "validate",
        message: `Total draw amount cannot exceed Available Funds (${formatted}).`,
      });
      toast.error(`Total draw amount exceeds Available Funds (${formatted}).`, {
        ...commonToast,
      });
      return;
    }
    if (!isValid)
      return toast.error(
        "Please complete all required fields before submitting.",
        { ...commonToast },
      );
    setShowConfirm(true);
  };

  const revalidateOne = (idx) =>
    setExcelRows((prev) =>
      prev.map((row, i) =>
        i === idx
          ? {
              ...validateRows([row], propertyList)[0],
              submitStatus: row.submitStatus,
              submitError: row.submitError,
            }
          : row,
      ),
    );
  const failedRows = useMemo(
    () => excelRows.filter((row) => row.submitStatus === "failed"),
    [excelRows],
  );
  const validRowsCount = excelRows.filter(
    (row) => row.status === "valid" && row.submitStatus !== "success",
  ).length;
  const invalidRowsCount = excelRows.filter(
    (row) => row.status === "invalid",
  ).length;
  const parseAmount = (value) =>
    Number(`${value || ""}`.replace(/[$,]/g, "")) || 0;
  const manualTotalAmount = lineItems.reduce(
    (sum, item) => sum + (Number(item.amount) || 0),
    0,
  );
  const availableFundsCap =
    typeof selectedProperty?.availableFundsValue === "number" &&
    Number.isFinite(selectedProperty.availableFundsValue)
      ? selectedProperty.availableFundsValue
      : null;
  const exceedsAvailableFunds =
    availableFundsCap !== null && manualTotalAmount > availableFundsCap;

  const submitValidRows = async () => {
    if (isMasquerading) {
      toast.error(MASQUERADE_SUBMIT_BLOCKED, { ...commonToast });
      return;
    }
    const findPropertyFromLoan = (loanNumber, loanRecId) => {
      const normalizedLoan = `${loanNumber || ""}`.trim().toLowerCase();
      const byAccount = propertyList.find(
        (property) =>
          `${property?.account || property?.Account || ""}`
            .trim()
            .toLowerCase() === normalizedLoan,
      );
      if (byAccount?.value) return byAccount.value;
      const byRecId = propertyList.find(
        (property) =>
          `${property?.loanRecId || property?.LoanRecid || property?.loanrecid || ""}`.trim() ===
          `${loanRecId || ""}`.trim(),
      );
      return byRecId?.value || null;
    };

    const targetIndexes = excelRows
      .map((r, i) => ({ r, i }))
      .filter(({ r }) => r.status === "valid" && r.submitStatus !== "success")
      .map(({ i }) => i);
    if (targetIndexes.length === 0) return;
    const workingRows = excelRows.map((row, i) =>
      targetIndexes.includes(i)
        ? { ...row, submitStatus: "submitting", submitError: "" }
        : row,
    );
    setSubmitLoader(true);
    setExcelProgress(
      `Submitting ${targetIndexes.length} draw request${targetIndexes.length === 1 ? "" : "s"}...`,
    );
    setExcelRows(workingRows);

    const submittedAt = format(new Date(), "HH:mm:ss");
    const linePayload = targetIndexes.map((index) => {
      const row = workingRows[index];
      return {
        lineId: null,
        requestedAmt: parseAmount(row.drawAmount),
        loanrecid: row.loanRecId,
        loanNumber: row.loanNo,
        draw_date: `${row.drawDate} ${submittedAt}`,
        property: findPropertyFromLoan(row.loanNo, row.loanRecId),
        drawNumber: null,
        notes: cleanNote(row.notes) || null,
      };
    });

    try {
      await axios.post(
        urls.draws.submitDrawRequest(),
        {
          line_items: linePayload,
          user_id: localStorage.getItem("apex_userid"),
        },
        getHeaders(),
      );
      const successRows = workingRows.map((row, i) =>
        targetIndexes.includes(i)
          ? { ...row, submitStatus: "success", submitError: "" }
          : row,
      );
      setExcelRows(successRows);
      toast.success(
        `${targetIndexes.length} draw request${targetIndexes.length === 1 ? "" : "s"} submitted successfully`,
        { ...commonToast },
      );
      closeModal();
      if (onSuccess) onSuccess();
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || "Submit failed";
      const failedRowsState = workingRows.map((row, i) =>
        targetIndexes.includes(i)
          ? { ...row, submitStatus: "failed", submitError: message }
          : row,
      );
      setExcelRows(failedRowsState);
      toast.error(`Failed to submit draw requests: ${message}`, {
        ...commonToast,
      });
    } finally {
      setSubmitLoader(false);
      setExcelProgress("");
    }
  };

  return (
    <>
      <Modal
        centered
        show={show && !showConfirm}
        onHide={closeModal}
        size={mode === "excel" ? "xl" : "lg"}
        dialogClassName={`draw-request-modal ${mode === "excel" ? "draw-excel-modal" : ""}`}
        backdrop="static"
      >
        <Modal.Header closeButton className="draw-modal-header">
          <div>
            <Modal.Title>New Draw Request</Modal.Title>
            <div className="modal-subtitle">
              Create a draw request manually or by Excel upload
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Nav
            variant="tabs"
            activeKey={mode}
            onSelect={(k) => setMode(k || "manual")}
          >
            <Nav.Item>
              <Nav.Link eventKey="manual">Manual</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="excel">Excel Upload</Nav.Link>
            </Nav.Item>
          </Nav>
          {mode === "manual" ? (
            <ManualDrawTab
              errors={errors}
              isLoadingProperties={isLoadingProperties}
              hasProjects={hasProjects}
              propertyList={propertyList}
              selectedProperty={selectedProperty}
              setSelectedProperty={setSelectedProperty}
              clearErrors={clearErrors}
              propertyAccountList={propertyAccountList}
              selectedAccount={selectedAccount}
              setSelectedAccount={setSelectedAccount}
              selectedDrawDate={selectedDrawDate}
              onDateChange={(date) => {
                if (!date) {
                  setSelectedDrawDate(null);
                  setDrawDate("");
                  return;
                }
                setSelectedDrawDate(date);
                setDrawDate(format(date, "MM/dd/yyyy"));
                clearErrors("drawDate");
              }}
              lineItems={lineItems}
              handleAddLine={() =>
                setLineItems((prev) => [
                  ...prev,
                  { id: Date.now(), budgetLine: null, amount: "" },
                ])
              }
              handleAmountChange={handleAmountChange}
              handleAmountBlur={handleAmountBlur}
              updateLineItem={updateLineItem}
              budgetLineOptions={budgetLineOptions}
              isLoadingLineItems={isLoadingLineItems}
              handleRemoveLine={(id) =>
                setLineItems((prev) => prev.filter((item) => item.id !== id))
              }
              handleManualSubmit={handleManualSubmit}
              manualNote={manualNote}
              setManualNote={setManualNote}
              noteMaxLength={NOTE_MAX_LENGTH}
              availableFundsCap={availableFundsCap}
              availableFundsFormatted={selectedProperty?.availableFunds}
              manualTotalAmount={manualTotalAmount}
              exceedsAvailableFunds={exceedsAvailableFunds}
              pendingAmount={selectedProperty?.pendingAmount || 0}
              pendingAmountFormatted={selectedProperty?.pendingAmountFormatted}
              pendingItems={selectedProperty?.pendingItems || []}
              pendingExceedsAvailable={
                (selectedProperty?.pendingAmount || 0) > 0 &&
                typeof selectedProperty?.originalAvailableFundsValue ===
                  "number" &&
                (selectedProperty?.pendingAmount || 0) >=
                  selectedProperty.originalAvailableFundsValue
              }
            />
          ) : (
            <ExcelUploadTab
              rows={excelRows}
              setRows={(updater) =>
                setExcelRows((prev) => {
                  const nextRows =
                    typeof updater === "function" ? updater(prev) : updater;
                  return isLoadingProperties
                    ? nextRows
                    : validateRows(nextRows, propertyList);
                })
              }
              propertyList={propertyList}
              noteMaxLength={NOTE_MAX_LENGTH}
              isMasquerading={isMasquerading}
              masqueradeBlockedMessage={MASQUERADE_SUBMIT_BLOCKED}
              loadingAccounts={isLoadingProperties}
              isSubmitting={submitLoader}
              onSubmitRequest={() =>
                invalidRowsCount > 0
                  ? setShowExcelConfirm(true)
                  : submitValidRows()
              }
              onCancelRows={() => setExcelRows([])}
              onRevalidateRow={revalidateOne}
              toastError={(message) => toast.error(message, { ...commonToast })}
              failedRows={failedRows}
              progressText={excelProgress}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        centered
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        size="md"
        backdrop="static"
        contentClassName="confirm-modal-content"
      >
        <Modal.Header closeButton className="confirm-modal-header">
          <div className="confirm-title">
            <div className="confirm-badge">Review</div>
            <Modal.Title>Confirm Draw Request</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="confirm-modal-body">
          <div className="confirm-summary">
            <div className="confirm-item">
              <div className="confirm-label">Property / Project</div>
              <div className="confirm-value">
                {selectedProperty?.value || "--"}
              </div>
            </div>
            <div className="confirm-item">
              <div className="confirm-label">Account</div>
              <div className="confirm-value">
                {selectedAccount?.value || "--"}
              </div>
            </div>
            <div className="confirm-item">
              <div className="confirm-label">Draw Date</div>
              <div className="confirm-value">{drawDate || "--"}</div>
            </div>
            <div className="confirm-item">
              <div className="confirm-label">Total Amount</div>
              <div className="confirm-value">
                ${manualTotalAmount.toFixed(2)}
              </div>
            </div>
            {cleanNote(manualNote) && (
              <div className="confirm-item confirm-item-note">
                <div className="confirm-label">Notes</div>
                <div className="confirm-value confirm-note-value">
                  {cleanNote(manualNote)}
                </div>
              </div>
            )}
          </div>
          <div className="confirm-warning">
            Please confirm the details before proceeding. Once submitted, the
            draw request cannot be retracted or canceled.
          </div>
          {isMasquerading && (
            <div className="masquerade-note">{MASQUERADE_SUBMIT_BLOCKED}</div>
          )}
        </Modal.Body>
        <Modal.Footer className="confirm-modal-footer">
          <Button
            variant="light"
            onClick={() => setShowConfirm(false)}
            disabled={submitLoader}
          >
            Cancel
          </Button>
          <Button
            className="confirm-primary"
            onClick={handleSubmitRequest}
            disabled={submitLoader || isMasquerading}
            title={isMasquerading ? MASQUERADE_SUBMIT_BLOCKED : undefined}
          >
            {submitLoader ? "Submitting..." : "Confirm"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        show={showExcelConfirm}
        onHide={() => setShowExcelConfirm(false)}
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Excel Submit</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {excelRows.length} rows uploaded, {validRowsCount} valid,{" "}
          {invalidRowsCount} invalid. Submit only the {validRowsCount} valid
          rows? Or fix the invalid rows first?
          {isMasquerading && (
            <div className="masquerade-note">{MASQUERADE_SUBMIT_BLOCKED}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowExcelConfirm(false)}
          >
            Fix first
          </Button>
          <Button
            onClick={() => {
              setShowExcelConfirm(false);
              submitValidRows();
            }}
            disabled={isMasquerading}
            title={isMasquerading ? MASQUERADE_SUBMIT_BLOCKED : undefined}
          >
            Submit {validRowsCount} valid
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default NewDrawRequestModal;
